import { baseApi } from '@store/baseApi';

import type {
  ClinicDocumentDetailResponse,
  ClinicDocumentListResponse,
  ClinicDocumentSectionsResponse,
  ClinicDocumentStatusResponse,
  ClinicDocumentUploadResponse,
  ExtractionResultResponse,
  SectionContentResponse,
  SectionDetailResponse,
} from '../types';

const CLINIC_DOCUMENTS_TAG_TYPES = [
  'ClinicDocument',
  'ClinicDocumentList',
  'ClinicDocumentSections',
  'ClinicDocumentSection',
  'ClinicDocumentSectionContent',
] as const;

export const clinicDocumentsApi = baseApi
  .enhanceEndpoints({
    addTagTypes: CLINIC_DOCUMENTS_TAG_TYPES,
  })
  .injectEndpoints({
    endpoints: builder => ({
      uploadClinicDocuments: builder.mutation<ClinicDocumentUploadResponse, { body: FormData }>({
        query: ({ body }) => ({
          url: '/v2/provider/clinic-documents',
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'ClinicDocumentList', id: 'LIST' }],
      }),
      listClinicDocuments: builder.query<
        ClinicDocumentListResponse,
        {
          scopeType?: string;
          docKind?: string;
          processingStatus?: string;
          search?: string;
          limit?: number;
          offset?: number;
        }
      >({
        query: ({ scopeType, docKind, processingStatus, search, limit, offset } = {}) => ({
          url: '/v2/provider/clinic-documents',
          params: {
            scope_type: scopeType,
            doc_kind: docKind,
            processing_status: processingStatus,
            search: search || undefined,
            limit,
            offset,
          },
        }),
        providesTags: result =>
          result
            ? [
                ...result.documents.map(document => ({
                  type: 'ClinicDocument' as const,
                  id: document.clinic_document_id,
                })),
                { type: 'ClinicDocumentList' as const, id: 'LIST' },
              ]
            : [{ type: 'ClinicDocumentList' as const, id: 'LIST' }],
      }),
      getClinicDocument: builder.query<ClinicDocumentDetailResponse, { clinicDocumentId: string }>({
        query: ({ clinicDocumentId }) => ({
          url: `/v2/provider/clinic-documents/${clinicDocumentId}`,
        }),
        providesTags: (_result, _error, { clinicDocumentId }) => [
          { type: 'ClinicDocument', id: clinicDocumentId },
        ],
      }),
      deleteClinicDocument: builder.mutation<
        ClinicDocumentStatusResponse,
        { clinicDocumentId: string }
      >({
        query: ({ clinicDocumentId }) => ({
          url: `/v2/provider/clinic-documents/${clinicDocumentId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, { clinicDocumentId }) => [
          { type: 'ClinicDocument', id: clinicDocumentId },
          { type: 'ClinicDocumentList', id: 'LIST' },
          { type: 'ClinicDocumentSections', id: clinicDocumentId },
        ],
      }),
      getClinicDocumentStatus: builder.query<ClinicDocumentStatusResponse, { clinicDocumentId: string }>({
        query: ({ clinicDocumentId }) => ({
          url: `/v2/provider/clinic-documents/${clinicDocumentId}/status`,
        }),
        providesTags: (_result, _error, { clinicDocumentId }) => [
          { type: 'ClinicDocument', id: clinicDocumentId },
        ],
      }),
      getClinicDocumentSections: builder.query<
        ClinicDocumentSectionsResponse,
        { clinicDocumentId: string }
      >({
        query: ({ clinicDocumentId }) => ({
          url: `/v2/provider/clinic-documents/${clinicDocumentId}/sections`,
        }),
        providesTags: (result, _error, { clinicDocumentId }) =>
          result
            ? [
                { type: 'ClinicDocumentSections' as const, id: clinicDocumentId },
                ...result.sections.map(section => ({
                  type: 'ClinicDocumentSection' as const,
                  id: section.clinic_document_section_id,
                })),
              ]
            : [{ type: 'ClinicDocumentSections' as const, id: clinicDocumentId }],
      }),
      getClinicDocumentSection: builder.query<SectionDetailResponse, { sectionId: string }>({
        query: ({ sectionId }) => ({
          url: `/v2/provider/clinic-documents/sections/${sectionId}`,
        }),
        providesTags: (result, _error, { sectionId }) =>
          result
            ? [
                { type: 'ClinicDocumentSection' as const, id: sectionId },
                ...(result.children ?? []).map(child => ({
                  type: 'ClinicDocumentSection' as const,
                  id: child.clinic_document_section_id,
                })),
              ]
            : [{ type: 'ClinicDocumentSection' as const, id: sectionId }],
      }),
      getClinicDocumentSectionContent: builder.query<SectionContentResponse, { sectionId: string }>({
        query: ({ sectionId }) => ({
          url: `/v2/provider/clinic-documents/sections/${sectionId}/content`,
        }),
        providesTags: (_result, _error, { sectionId }) => [
          { type: 'ClinicDocumentSectionContent', id: sectionId },
        ],
      }),
      getClinicDocumentFile: builder.query<
        Blob,
        { clinicDocumentId: string; disposition?: 'inline' | 'attachment' }
      >({
        query: ({ clinicDocumentId, disposition }) => ({
          url: `/v2/provider/clinic-documents/${clinicDocumentId}/file`,
          params: { disposition },
          responseHandler: async (response: Response) => response.blob(),
        }),
      }),
    }),
    overrideExisting: false,
  });

// ---------------------------------------------------------------------------
// Admin endpoints (read-only, moderator auth)
// All scoped by organization_id path parameter.
// ---------------------------------------------------------------------------
const ADMIN_BASE = '/v2/admin/organizations';

interface AdminOrgArg {
  organizationId: string;
}

interface AdminDocArg extends AdminOrgArg {
  clinicDocumentId: string;
}

export const adminClinicDocumentsApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      'AdminClinicDocument',
      'AdminClinicDocumentList',
      'AdminClinicDocumentSections',
      'AdminClinicDocumentSection',
      'AdminClinicDocumentSectionContent',
    ] as const,
  })
  .injectEndpoints({
    endpoints: builder => ({
      uploadAdminClinicDocuments: builder.mutation<
        ClinicDocumentUploadResponse,
        AdminOrgArg & { body: FormData }
      >({
        query: ({ organizationId, body }) => ({
          url: `${ADMIN_BASE}/${organizationId}/clinic-documents`,
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'AdminClinicDocumentList', id: 'LIST' }],
      }),
      listAdminClinicDocuments: builder.query<
        ClinicDocumentListResponse,
        AdminOrgArg & {
          scopeType?: string;
          docKind?: string;
          processingStatus?: string;
          search?: string;
          limit?: number;
          offset?: number;
        }
      >({
        query: ({ organizationId, scopeType, docKind, processingStatus, search, limit, offset }) => ({
          url: `${ADMIN_BASE}/${organizationId}/clinic-documents`,
          params: {
            scope_type: scopeType,
            doc_kind: docKind,
            processing_status: processingStatus,
            search: search || undefined,
            limit,
            offset,
          },
        }),
        providesTags: result =>
          result
            ? [
                ...result.documents.map(doc => ({
                  type: 'AdminClinicDocumentList' as const,
                  id: doc.clinic_document_id,
                })),
                { type: 'AdminClinicDocumentList' as const, id: 'LIST' },
              ]
            : [{ type: 'AdminClinicDocumentList' as const, id: 'LIST' }],
      }),
      getAdminClinicDocument: builder.query<ClinicDocumentDetailResponse, AdminDocArg>({
        query: ({ organizationId, clinicDocumentId }) => ({
          url: `${ADMIN_BASE}/${organizationId}/clinic-documents/${clinicDocumentId}`,
        }),
        providesTags: (_result, _error, { clinicDocumentId }) => [
          { type: 'AdminClinicDocument', id: clinicDocumentId },
        ],
      }),
      getAdminClinicDocumentStatus: builder.query<ClinicDocumentStatusResponse, AdminDocArg>({
        query: ({ organizationId, clinicDocumentId }) => ({
          url: `${ADMIN_BASE}/${organizationId}/clinic-documents/${clinicDocumentId}/status`,
        }),
      }),
      getAdminClinicDocumentFile: builder.query<
        Blob,
        AdminDocArg & { disposition?: 'inline' | 'attachment' }
      >({
        query: ({ organizationId, clinicDocumentId, disposition }) => ({
          url: `${ADMIN_BASE}/${organizationId}/clinic-documents/${clinicDocumentId}/file`,
          params: { disposition },
          responseHandler: async (response: Response) => response.blob(),
        }),
      }),
      getAdminClinicDocumentSections: builder.query<ClinicDocumentSectionsResponse, AdminDocArg>({
        query: ({ organizationId, clinicDocumentId }) => ({
          url: `${ADMIN_BASE}/${organizationId}/clinic-documents/${clinicDocumentId}/sections`,
        }),
        providesTags: (result, _error, { clinicDocumentId }) =>
          result
            ? [
                { type: 'AdminClinicDocumentSections' as const, id: clinicDocumentId },
                ...result.sections.map(s => ({
                  type: 'AdminClinicDocumentSection' as const,
                  id: s.clinic_document_section_id,
                })),
              ]
            : [{ type: 'AdminClinicDocumentSections' as const, id: clinicDocumentId }],
      }),
      getAdminClinicDocumentExtractionResult: builder.query<
        ExtractionResultResponse,
        AdminDocArg & { taskName: string }
      >({
        query: ({ organizationId, clinicDocumentId, taskName }) => ({
          url: `${ADMIN_BASE}/${organizationId}/clinic-documents/${clinicDocumentId}/extraction-results/${taskName}`,
        }),
      }),
      getAdminClinicDocumentSection: builder.query<
        SectionDetailResponse,
        AdminOrgArg & { sectionId: string }
      >({
        query: ({ organizationId, sectionId }) => ({
          url: `${ADMIN_BASE}/${organizationId}/clinic-documents/sections/${sectionId}`,
        }),
        providesTags: (result, _error, { sectionId }) =>
          result
            ? [
                { type: 'AdminClinicDocumentSection' as const, id: sectionId },
                ...(result.children ?? []).map(child => ({
                  type: 'AdminClinicDocumentSection' as const,
                  id: child.clinic_document_section_id,
                })),
              ]
            : [{ type: 'AdminClinicDocumentSection' as const, id: sectionId }],
      }),
      getAdminClinicDocumentSectionContent: builder.query<
        SectionContentResponse,
        AdminOrgArg & { sectionId: string }
      >({
        query: ({ organizationId, sectionId }) => ({
          url: `${ADMIN_BASE}/${organizationId}/clinic-documents/sections/${sectionId}/content`,
        }),
        providesTags: (_result, _error, { sectionId }) => [
          { type: 'AdminClinicDocumentSectionContent', id: sectionId },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useDeleteClinicDocumentMutation,
  useGetClinicDocumentFileQuery,
  useGetClinicDocumentQuery,
  useGetClinicDocumentSectionContentQuery,
  useGetClinicDocumentSectionsQuery,
  useGetClinicDocumentSectionQuery,
  useGetClinicDocumentStatusQuery,
  useLazyGetClinicDocumentStatusQuery,
  useListClinicDocumentsQuery,
  useUploadClinicDocumentsMutation,
} = clinicDocumentsApi;

export const {
  useUploadAdminClinicDocumentsMutation,
  useListAdminClinicDocumentsQuery,
  useGetAdminClinicDocumentQuery,
  useGetAdminClinicDocumentStatusQuery,
  useGetAdminClinicDocumentFileQuery,
  useGetAdminClinicDocumentSectionsQuery,
  useGetAdminClinicDocumentExtractionResultQuery,
  useGetAdminClinicDocumentSectionQuery,
  useGetAdminClinicDocumentSectionContentQuery,
} = adminClinicDocumentsApi;
